<template>
  <Transition
    enter-active-class="transition-opacity duration-300 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      ref="tooltipRef"
      :class="[
        'tooltip-box',
        responsiveSizeClasses,
        centerContent ? 'justify-center !pb-5' : ''
      ]"
      :style="responsiveSizeStyles"
    >
      <div v-if="title || description" class="flex w-full flex-col gap-1">
        <h3 v-if="title" class="break-words text-base font-bold text-gray-900">
          {{ title }}
        </h3>
        <p v-if="description" class="break-words text-sm text-gray-900">
          {{ description }}
        </p>
      </div>
      <div
        v-if="arrowPosition !== 'none'"
        :class="['absolute bottom-0 translate-y-full', arrowPositionClasses]"
      >
        <IconTooltipArrow />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import { useDelayedClickOutside } from '@/shared/composables/useDelayedClickOutside';

import IconTooltipArrow from './Icons/IconTooltipArrow.vue';

type ArrowPosition = 'left' | 'center' | 'right' | 'none';

const props = withDefaults(
  defineProps<{
    show?: boolean;
    title?: string;
    description?: string;
    arrowPosition?: ArrowPosition;
    width?: number;
    height?: number;
    backgroundColor?: string;
    centerContent?: boolean;
  }>(),
  {
    show: true,
    title: undefined,
    description: undefined,
    arrowPosition: 'center',
    width: undefined,
    height: undefined,
    backgroundColor: undefined,
    centerContent: false
  }
);

const emit = defineEmits<{
  hide: [];
}>();

const tooltipRef = useTemplateRef('tooltipRef');

const arrowPositionClasses = computed(() => {
  switch (props.arrowPosition) {
    case 'left':
      return 'left-4';
    case 'right':
      return 'right-3.5';
    case 'none':
      return '';
    case 'center':
    default:
      return 'left-1/2 -translate-x-1/2';
  }
});

const responsiveSizeClasses = computed(() => {
  if (!props.width && !props.height) {
    return '';
  }

  const classes: string[] = [];

  if (props.width) {
    // Mobile: full width with padding, tablet: smaller fixed width
    classes.push('w-[calc(100vw-2rem)] sm:w-[320px]');
  }

  if (props.height) {
    // Responsive height: auto on mobile
    classes.push('h-auto min-h-[80px] sm:h-auto');
  }

  return classes.join(' ');
});

const responsiveSizeStyles = computed(() => {
  const styles: Record<string, string> = {};

  if (props.width) {
    // Use prop value for md+ breakpoint via CSS custom property
    styles['--tooltip-width-md'] = `${props.width}px`;
  }

  if (props.height) {
    // Use prop value as min-height for md+ breakpoint via CSS custom property
    styles['--tooltip-height-md'] = `${props.height}px`;
  }

  if (props.backgroundColor) {
    styles['background-color'] = props.backgroundColor;
  }

  return Object.keys(styles).length > 0 ? styles : undefined;
});

// Handle click outside with delay to prevent immediate closing
useDelayedClickOutside(tooltipRef, () => emit('hide'));
</script>

<style scoped>
@media (min-width: 768px) {
  .tooltip-box {
    width: var(--tooltip-width-md);
    min-height: var(--tooltip-height-md);
  }
}
</style>
